import { BAY_HIRE_BFF_SERVICE_SEGMENT } from "@config/apiConfig";
import { api } from "./api";
import { uploadFileToAzureBlob } from "./azureDataApi";
import { SasTokenPrefix, SasTokenPrivilege } from "./user/types";
import { getSasToken } from "./userApi";

export { BAY_HIRE_BFF_SERVICE_SEGMENT };

export interface BayHireRequestCreate {
  bayPreference: "INSIDE_PORT" | "OUTSIDE_PORT";
  // DFT-0110: which port entity this request belongs to - POF (Port of Fujairah) or POD (Port of
  // Dibba). Required and immutable thereafter - neither ResubmitRequest nor VendorUpdateRequest
  // below carries it.
  port: "POF" | "POD";
  dateFrom: string;
  dateTo: string;
  paymentType: "MONTHLY" | "YEARLY" | "EXEMPTED";
  responsiblePersonName: string;
  responsiblePersonDesignation: string;
  remarks?: string;
  // DFT-0044 slice U (D39): staged documents to attach at creation. Only ever needed for a
  // paymentType EXEMPTED request today - HOD_APPROVAL is the only attachmentType the domain acts on -
  // but the field itself is not restricted to it. Staged via uploadStagedBayHireAttachment BEFORE the
  // request exists (there is no request id yet to attach to).
  attachments?: StagedAttachment[];
  // DFT-0044 slice C: the bays the Shipping Agent selected. Only referenceDataBayId is honoured -
  // the BFF re-derives bayNoSnapshot, areaSnapshot and criteriaSnapshot from Reference Data and
  // discards anything a client sends, because the domain validates inside/outside consistency
  // AGAINST areaSnapshot and a client-supplied value would mean the client validating itself.
  bayAssignments: BayAssignmentInput[];
  // numberOfBays is DERIVED from bayAssignments.length server-side and is no longer sent. It was a
  // vendor-declared count shown to Operations as a reference during pre-approval (DFT-0013);
  // Operations no longer selects bays, so there is nothing for it to be a reference for.
}

// DFT-0013 (AD-05): a single Reference-Data-sourced bay selection.
export interface BayAssignmentInput {
  referenceDataBayId: string;
  bayNoSnapshot?: string;
  areaSnapshot?: string;
  criteriaSnapshot?: Record<string, unknown>;
}

export interface BayAssignment {
  id: string;
  referenceDataBayId: string;
  bayNoSnapshot?: string | null;
  areaSnapshot?: string | null;
  criteriaSnapshot?: Record<string, unknown> | null;
  createdAt: string;
}

// DFT-0013 (AD-04 phase 1): HOD approval document metadata.
export interface AttachmentUploadRequest {
  attachmentType: string;
  documentUrl: string;
  originalFileName?: string;
  contentType?: string;
  fileSizeBytes?: number;
  documentStatus?: string;
}

/**
 * DFT-0044 slice U: a document staged via `uploadStagedAttachment`, before the request exists.
 *
 * <p>DFT-0050: `documentUrl` is a `temp/bay-hire/{random}` blob written directly to Azure by the
 * browser via a SAS token (never proxied through any Bay Hire backend service). No per-caller
 * ownership binding is enforced on this reference server-side - this is a deliberate regression
 * from the prior company-prefix check (D37), matching the platform's own convention exactly (see
 * the DFT-0050 decision log update).
 */
export interface StagedAttachment {
  attachmentType: string;
  documentUrl: string;
  originalFileName?: string;
  contentType?: string;
  fileSizeBytes?: number;
  /**
   * DFT-0050 (AC34/AC35): only ever present on the Update Request path (the requestId branch of
   * `BayHireCreateAttachmentUpload.handleUpload`), where the upload is a real, already-persisted
   * `Attachment` - never on the create-time staged path, where the blob has no domain-side row
   * yet. Lets the edit form's submit handler tell `useBayHireContainer`'s vendorUpdate call
   * whether the HOD document changed (and to what), without which a replace/remove decision made
   * during the SAME edit would have nothing to reference.
   */
  id?: string;
}

export interface Attachment {
  id: string;
  attachmentType: string;
  documentUrl: string;
  originalFileName?: string | null;
  contentType?: string | null;
  fileSizeBytes?: number | null;
  uploadedBy?: string | null;
  uploadedByRole?: string | null;
  uploadedAt?: string | null;
  documentStatus?: string | null;
}

// DFT-0013 Operations pre-approval decision payload.
export interface PreApproveRequest {
  dateFromApproved: string;
  dateToApproved: string;
  totalAmount: number;
  // DFT-0044: chargeable and bayAssignments are GONE from the pre-approve contract. chargeable is
  // derived from the request's paymentType server-side (D42) and Operations no longer selects bays -
  // the Shipping Agent does, at request time, and they are immutable thereafter (D35/D43).
  hodAttachmentId?: string | null;
  preApprovalRemarks?: string;
}

// DFT-0084 Operations direct-approve decision payload for EXEMPTED requests only. Same shape as
// PreApproveRequest minus totalAmount, which is never meaningful for a non-chargeable request.
export interface ApproveExemptedRequest {
  dateFromApproved: string;
  dateToApproved: string;
  hodAttachmentId?: string | null;
  preApprovalRemarks?: string;
}

// DFT-0013 (AD-11): Reference Data bay option for the pre-approval combobox.
export interface BayHireBayCriterion {
  code: string;
  label?: string | null;
  value?: string | null;
  displayOrder?: number | null;
}

export interface BayHireBayOption {
  id: string;
  bayNo: string;
  displayLabel: string;
  active: boolean;
  area?: string | null;
  areaCode?: string | null;
  bayTypeCode?: string | null;
  bayTypeLabel?: string | null;
  criteria?: BayHireBayCriterion[] | null;
  displayOrder?: number | null;
  // DFT-0110: which port entity this bay belongs to. Not read by the SPA today - the catalogue
  // is already scoped server-side by the `portCode` query param `getBayOptions` sends - but
  // declared for completeness/forward compatibility with the actual response shape.
  portCode?: string | null;
}

export interface BayHireRequest {
  id: string;
  // DFT-0044 slice T: the human-facing reference number ("123"), distinct from `id` (a UUID).
  // `05_Port_License_details-15.png` uses this as the detail page's heading and breadcrumb value -
  // the SPA's own type never carried it despite the backend returning it (BIGSERIAL, D52), so
  // every screen that should show it was still showing the UUID instead.
  requestNo?: number | null;
  companyId: string;
  status: string;
  bayPreference: string;
  // DFT-0110: which port entity this request belongs to - POF or POD. Set once at creation,
  // immutable thereafter.
  port: string;
  dateFrom: string;
  dateTo: string;
  paymentType: string;
  responsiblePersonName: string;
  responsiblePersonDesignation: string;
  remarks?: string;
  numberOfBays?: number | null;
  createdAt: string;
  updatedAt: string;
  // DFT-0009: workflow correlation + task snapshot (nullable).
  workflowId?: string | null;
  workflowRunId?: string | null;
  workflowNamespace?: string | null;
  currentTaskId?: string | null;
  currentTaskStatus?: string | null;
  currentTaskOwner?: string | null;
  currentTaskModifiedOn?: string | null;
  // DFT-0013: pre-approval fields (nullable until pre-approved).
  totalAmount?: number | null;
  chargeable?: boolean | null;
  dateFromApproved?: string | null;
  dateToApproved?: string | null;
  preApprovalRemarks?: string | null;
  bayAssignments?: BayAssignment[] | null;
  attachments?: Attachment[] | null;
  // DFT-0010: rejection/cancellation reasons (nullable; visible to Vendor on request detail).
  rejectionReason?: string | null;
  cancellationReason?: string | null;
  // DFT-0014: payment receipt, present from PAYED onward. Null before
  // PRE_APPROVED -> PAYED, and after :resubmit clears a rejected receipt.
  receipt?: Receipt | null;
  // DFT-0015: manual waiting-list metadata. Present once the request has
  // been added to WAITING_LIST at least once - retained after the request
  // exits WAITING_LIST (e.g. promoted to PRE_APPROVED), so callers must
  // check status === "WAITING_LIST" to know whether this reflects the
  // request's current, active state.
  waitingListEntry?: WaitingListEntry | null;
  // DFT-0027: Port Operator company details, resolved by the BFF from companyId
  // (a reference-data company code) for the request detail's "Company details"
  // card. Optional: the BFF leaves it unset when the reference-data lookup
  // fails, so the detail page still renders.
  company?: CompanyDetails | null;
}

/**
 * DFT-0044 (Q13, answered 2026-07-29): `fax` is REINSTATED here, reversing an earlier descoping -
 * `reference.company` genuinely has no fax column, so it is present on the contract per the answer
 * while staying unpopulated. Expect it to always deserialize as `null` until a source exists
 * (Q13-a); that is a recorded decision, not a bug.
 *
 * <p>Shared between `BayHireRequest.company` (the detail page, resolved from an existing request's
 * companyId) and `getMyCompanyDetails` (the create form, resolved from the caller's own token,
 * before a request exists) - both are the identical shape from the identical BFF schema.
 */
export interface CompanyDetails {
  companyNameEnglish?: string | null;
  proOfficeNumber?: string | null;
  poBox?: string | null;
  proEmail?: string | null;
  emirates?: string | null;
  fax?: string | null;
  // Prefill support (2026-08-05): the create form's "Name of Port company responsible" /
  // "Designation in the company" DEFAULTS, sourced from the caller's own APPROVED Port Operator
  // License (license-service), NOT Reference Data - unlike every other field on this interface.
  // Best-effort like the rest: null whenever no license, an unapproved one, or an ambiguous
  // (more than one approved) match is found. Unlike the read-only fields above, these only seed a
  // starting value for an editable field - the vendor can still correct it if the license record
  // is stale or wrong.
  responsiblePersonName?: string | null;
  responsiblePersonDesignation?: string | null;
}

// DFT-0015 manual waiting-list metadata (present on BayHireRequest from
// WAITING_LIST onward; retained after exit - see BayHireRequest.waitingListEntry).
export interface WaitingListEntry {
  id: string;
  dateAdded: string;
  priority: number;
  createdBy?: string | null;
  createdByPreferredUsername?: string | null;
  createdAt: string;
}

// Paginated response envelope for GET /bay-hire-requests, following the
// gatepass-service/security-clearance pagination convention.
export interface PaginatedBayHireRequestDTO {
  totalRecordCount: number;
  records: BayHireRequest[];
}

// DFT-0014 payment receipt metadata (present on BayHireRequest from PAYED onward).
export interface Receipt {
  id: string;
  receiptNumber: string;
  receiptDate: string;
  receiptAmount: number;
  attachmentId: string;
  createdBy?: string | null;
  createdByRole?: string | null;
  createdAt: string;
}

// DFT-0014 Vendor payment-receipt upload payload.
export interface UploadReceiptRequest {
  receiptNumber: string;
  receiptDate: string;
  receiptAmount: number;
  attachmentId: string;
}

// DFT-0010 reject/cancel request bodies (mandatory reason, min 1/max 1000 chars).
// DFT-0044 slice F (F3): attachmentId is optional - a reference to a previously uploaded
// SUPPORTING_DOCUMENT attachment, same two-phase pattern as PreApproveRequest's hodAttachmentId.
export interface RejectRequest {
  reason: string;
  attachmentId?: string;
}

export interface CancelRequest {
  reason: string;
  attachmentId?: string;
}

// DFT-0010 resubmit request body: same fields as initial submission.
// Megaplan (2026-08-05, item 10): bayAssignments added - it was missing entirely, so a vendor
// changing their bay selection on resubmit had it silently dropped, even though the domain's own
// ResubmitCommand now supports replacing the selection (mirrors VendorUpdateRequest's own field).
export interface ResubmitRequest {
  dateFrom: string;
  dateTo: string;
  /**
   * DFT-0087: previously absent entirely - the Resubmit form already renders Payment Type as
   * editable (unlike Update, where it's locked), but this field was never read into the
   * submitted payload, so a vendor's changed payment type was silently dropped even though the
   * API returned 202. Required, not omissible - resubmit uses the same schema as submission, so
   * paymentType is always resent, matching bayPreference/numberOfBays.
   */
  paymentType: BayHirePaymentType;
  bayPreference: "INSIDE_PORT" | "OUTSIDE_PORT";
  numberOfBays: number;
  responsiblePersonName: string;
  responsiblePersonDesignation: string;
  bayAssignments?: BayAssignmentInput[];
  remarks?: string;
  /**
   * DFT-0050 (bug fix, 2026-08-26; refactored same day per requester direction): mirrors
   * VendorUpdateRequest's own hodAttachmentId field exactly, including its single-field
   * keep/replace/remove semantics - previously there was no wire path for a resubmit to replace
   * or remove the request's existing HOD approval document, which is also why the Resubmit form
   * appeared to lose it entirely. Always send the form's current value (the request's existing
   * id to keep it, a new id to replace it, or null to remove it) - there is no "omit to leave
   * alone" state for this field.
   */
  hodAttachmentId?: string | null;
}

// DFT-0044 slice V (D43 revised): the field set a Vendor may change on their own request before a
// decision (PATCH /bay-hire-requests/{id}). paymentType remains deliberately absent - the BFF's
// own VendorUpdateRequest omits it by design (payment-type immutability is load-bearing).
// bayAssignments is accepted: when supplied it REPLACES the request's entire bay selection, not
// merged with it - only referenceDataBayId needs to be sent, the BFF re-derives every other
// snapshot from Reference Data exactly as it does at creation.
//
// DFT-0061: bayPreference is now conditionally accepted - omit it to leave unchanged (same PATCH
// semantics as every other field here). The BFF/domain enforce the empty-list-or-replacement
// precondition; this type does not encode that constraint itself.
export interface VendorUpdateRequest {
  dateFrom?: string;
  dateTo?: string;
  responsiblePersonName?: string;
  responsiblePersonDesignation?: string;
  bayPreference?: "INSIDE_PORT" | "OUTSIDE_PORT";
  bayAssignments?: BayAssignmentInput[];
  remarks?: string;
  /**
   * DFT-0050 (AC34/AC35, refactored 2026-08-26 per requester direction): the request's current
   * HOD approval document id. Always send the form's current value (the existing id to keep it,
   * a new id to replace it, or null to remove it) - there is no "omit to leave alone" state for
   * this field, the same full-replace idiom bayAssignments above already uses.
   */
  hodAttachmentId?: string | null;
}

// DFT-0009 :start response.
export type WorkflowStartStatus = "STARTED" | "FAILED_TO_START";

export interface BayHireStartResponse {
  request: BayHireRequest;
  workflowStartStatus: WorkflowStartStatus;
  workflowId?: string | null;
}

// DFT-0009 Operations queue.
export interface BayHireTaskView {
  taskId?: string;
  status?: string;
  owner?: string | null;
  modifiedOn?: string | null;
}

export interface BayHireQueueRow {
  request: BayHireRequest;
  task?: BayHireTaskView | null;
}

export interface BayHireQueueSearch {
  ids?: string[];
  status?: "REQUESTED" | "WAITING_LIST";
  taskStatus?: string[];
  // Top-N bound (ordered by createdAt DESC).
  limit?: number;
}

// DFT-0069: fields available on the generic filter-criteria DSL, mirroring the domain's own
// BayHireSearchField exactly (bay-hire-api.yaml) - camelCase wire values, matching
// vessel-call-management-service's own VesselCallSearchField convention (`vesselId`,
// `agencyName`, `statusNotIn`, etc. - never UPPER_SNAKE_CASE).
// DFT-0075: dateFrom/dateTo is a containment filter - only requests whose entire hire period
// falls inside [dateFrom, dateTo] match: request.dateFrom >= filter.dateFrom AND request.dateTo
// <= filter.dateTo - either bound can be omitted independently.
export type BayHireSearchField =
  | "status"
  | "paymentType"
  | "bayPreference"
  | "requestNo"
  | "numberOfBays"
  | "responsiblePersonName"
  | "dateFrom"
  | "dateTo"
  | "createdAtFrom"
  | "createdAtTo";

// DFT-0069: one filter entry - `values` OR-combines (a request matches if ANY value matches
// for a discrete field; dateFrom/dateTo/createdAtFrom/createdAtTo accept exactly one value
// each, enforced server-side). Multiple FilterItems sharing the same field are merged into one
// entry's `values` array by buildFiltersFromUI (useBayHireRequestList.tsx/
// useBayHireOperationsQueue.tsx) - mirrors vessel-call-management-service's own
// PortCalls.tsx/utils.ts buildFilterPayload exactly, including the merge-by-field behavior.
export interface BayHireSearchFilter {
  field: BayHireSearchField;
  values: string[];
}

// DFT-0069: replaces the 8 named query params (GET /bay-hire-requests) with a single generic
// body, mirroring the domain's own BayHireSearchCriteria exactly. Built directly by
// useBayHireRequestList.tsx's buildFiltersFromUI - no intermediate named-field type, matching
// vessel-call-management-service/PortCalls.tsx's own getSearchVesselCalls precedent (its
// payload is `{ pageSize, pageNumber, filters: {field, values}[] }` verbatim, not a named
// per-field DTO).
export interface BayHireSearchCriteria {
  pageNumber: number;
  pageSize: number;
  filters?: BayHireSearchFilter[];
}

// DFT-0037/DFT-0069: dedicated offset-paginated variant of BayHireQueueSearch (split from it,
// mirroring the same split on the BFF/domain), used by searchQueuePaged. pageNumber is
// 0-based (matches the backend contract). `filters` is the same generic
// BayHireSearchFilter[] as BayHireSearchCriteria - built by useBayHireOperationsQueue.tsx's
// own buildFiltersFromUI, including a `status` entry (DFT-0040: OR's multiple statuses, e.g.
// ["REQUESTED", "PAYED"], in one query) once the tab's own allowed-status intersection is
// resolved. `ids`/`taskStatus` are not filter entries - they stay top-level, unchanged.
export interface BayHireQueueSearchPaged {
  ids?: string[];
  taskStatus?: string[];
  filters?: BayHireSearchFilter[];
  pageNumber: number;
  pageSize: number;
}

// DFT-0037: paginated response envelope for POST /bay-hire-requests/search-paged,
// following the same {totalRecordCount, records} shape as
// PaginatedBayHireRequestDTO (GET /bay-hire-requests).
export interface PaginatedBayHireQueueRowDTO {
  totalRecordCount: number;
  records: BayHireQueueRow[];
}

// DFT-0015 Operations add-to-waiting-list payload. Both fields optional -
// the backend defaults priority to the next available manual-order slot and
// reason to "ADDED_TO_WAITING_LIST" when omitted.
export interface AddToWaitingListRequest {
  priority?: number;
  reason?: string;
}

// DFT-0033: Vendor request list Filters panel. Every field optional/
// AND-combined - see bay-hire-service's ListFilters/searchByCompanyId.
// createdAtFrom/createdAtTo are "Date submitted" range bounds (ISO date,
// e.g. "2026-07-01").
//
// DFT-0027 (Slice G): the paymentType/bayPreference unions are shared with the Operations
// tabs' Filters panel - both panels offer the same domain values.
export type BayHirePaymentType = "MONTHLY" | "YEARLY" | "EXEMPTED";
export type BayHireBayPreference = "INSIDE_PORT" | "OUTSIDE_PORT";

// Megaplan (2026-08-05, item 6): extracted so useBayHireOperationsQueue.tsx's status
// resolution and useBayHireQueuePaged's own param can share this instead of re-declaring the
// same 7-value union a third time - previously only this list's own `status` filter had the full set;
// the Operations queue's `statuses` was still restricted to ["REQUESTED","PAYED","WAITING_LIST"].
export type BayHireRequestStatus =
  | "REQUESTED"
  | "PRE_APPROVED"
  | "PAYED"
  | "APPROVED"
  | "REJECTED"
  | "CANCELLED"
  | "WAITING_LIST";

// DFT-0045: server-generated PDF print document, matching
// getGatepassPrintDocument/getSecurityClearancePrintDocument's exact shape -
// both fetch a `:print` BFF endpoint as a blob for printRequestedTemplate.ts
// to open and print. Standalone export (not part of bayHireService) to match
// that same precedent.
export const getBayHirePrintDocument = async ({
  service,
  id,
}: {
  service: string;
  id: string;
}): Promise<Blob> => {
  return api.get<Blob>(service, `bay-hire-requests/${id}:print`, {
    responseType: "blob",
  });
};

export const bayHireService = {
  createRequest: async (request: BayHireRequestCreate): Promise<BayHireRequest> => {
    return api.post<BayHireRequest>(BAY_HIRE_BFF_SERVICE_SEGMENT, "bay-hire-requests", request);
  },

  // DFT-0025: paginated (pageNumber is 0-based, matching the backend contract).
  // DFT-0033: filters (optional) power the Vendor request list's Filters panel.
  // DFT-0069: moved from GET /bay-hire-requests + 8 named query params to POST
  // /bay-hire-requests:list with a generic BayHireSearchCriteria body. `filters` is the
  // generic BayHireSearchFilter[] built directly by useBayHireRequestList.tsx's
  // buildFiltersFromUI - this is a pure pass-through, no per-field translation here,
  // mirroring vessel-call-management-service/PortCalls.tsx's own getSearchVesselCalls.
  listRequests: async (
    pageNumber = 0,
    pageSize = 25,
    filters: BayHireSearchFilter[] = [],
  ): Promise<PaginatedBayHireRequestDTO> => {
    const criteria: BayHireSearchCriteria = {
      pageNumber,
      pageSize,
      ...(filters.length > 0 ? { filters } : {}),
    };
    return api.post<PaginatedBayHireRequestDTO>(
      BAY_HIRE_BFF_SERVICE_SEGMENT,
      "bay-hire-requests:list",
      criteria,
    );
  },

  getRequestById: async (id: string): Promise<BayHireRequest> => {
    return api.get<BayHireRequest>(BAY_HIRE_BFF_SERVICE_SEGMENT, `bay-hire-requests/${id}`);
  },

  // DFT-0009 Vendor submission entry point: creates the request and starts the workflow.
  // The response reports workflowStartStatus; callers should render a warning for FAILED_TO_START (AC46).
  // DFT-0118: optional transactionId, propagated from useStartBayHireRequest's runTransaction call.
  startRequest: async (
    request: BayHireRequestCreate,
    transactionId?: string,
  ): Promise<BayHireStartResponse> => {
    return api.post<BayHireStartResponse>(
      BAY_HIRE_BFF_SERVICE_SEGMENT,
      "bay-hire-requests:start",
      request,
      { transactionId },
    );
  },

  // DFT-0044 slice U, rewritten by DFT-0050 (AD1/AD3): stages a document BEFORE the request
  // exists - there is no request id yet to attach it to. Matches the platform's SAS pattern
  // (e.g. useUploadOperationsDocuments): mint a WRITE SAS token scoped to BAY_HIRE, then upload
  // the bytes directly to Azure Blob Storage - no bytes transit any Bay Hire backend service.
  // The returned reference is submitted inside the create payload's `attachments`, which
  // relocates the blob out of temp/ once the request id exists (revised AD4).
  uploadStagedAttachment: async (file: File, attachmentType: string): Promise<StagedAttachment> => {
    const { blobUrl, sasToken } = await getSasToken({
      prefix: SasTokenPrefix.BAY_HIRE,
      contentType: file.type || null,
      privilege: SasTokenPrivilege.WRITE,
      blobUrl: null,
    });
    await uploadFileToAzureBlob({ file, blobUrl, sasToken, certificateType: attachmentType });
    return {
      attachmentType,
      documentUrl: blobUrl,
      originalFileName: file.name,
      contentType: file.type || undefined,
      fileSizeBytes: file.size,
    };
  },

  // DFT-0009 Operations queue search.
  searchQueue: async (search: BayHireQueueSearch = {}): Promise<BayHireQueueRow[]> => {
    return api.post<BayHireQueueRow[]>(
      BAY_HIRE_BFF_SERVICE_SEGMENT,
      "bay-hire-requests/search",
      search,
    );
  },

  // DFT-0037 (originally DFT-0015): offset-paginated variant of
  // searchQueue, additive alongside it. Calls the BFF's dedicated
  // search-paged endpoint, which returns the total matching row count
  // across all pages in the response body (PaginatedBayHireQueueRowDTO)
  // instead of an X-Total-Count response header, aligning with the
  // platform-wide body-wrapped pagination convention.
  //
  // DFT-0027 (Slice G): the Operations tabs' Filters panel adds
  // paymentType/bayPreference/createdAtFrom/createdAtTo.
  // DFT-0069: `filters` is the generic BayHireSearchFilter[] built directly by
  // useBayHireOperationsQueue.tsx's buildFiltersFromUI (statuses folds into a `status`
  // filter entry there, alongside the rest) - pure pass-through here, no per-field
  // translation. Omitted from the request body when unset/empty, so an unfiltered queue
  // call sends no `filters` key at all.
  searchQueuePaged: async (
    search: BayHireQueueSearchPaged,
  ): Promise<{ rows: BayHireQueueRow[]; totalCount: number }> => {
    const { ids, taskStatus, filters, pageNumber, pageSize } = search;
    const body = {
      ...(ids && ids.length > 0 ? { ids } : {}),
      ...(taskStatus && taskStatus.length > 0 ? { taskStatus } : {}),
      ...(filters && filters.length > 0 ? { filters } : {}),
      pageNumber,
      pageSize,
    };
    const result = await api.post<PaginatedBayHireQueueRowDTO>(
      BAY_HIRE_BFF_SERVICE_SEGMENT,
      "bay-hire-requests/search-paged",
      body,
    );
    return {
      rows: result.records ?? [],
      totalCount: result.totalRecordCount ?? (result.records ?? []).length,
    };
  },

  // DFT-0015: Operations manually defers a claimed REQUESTED request to the
  // waiting list. Requires an active CLAIMED task, same as reject/cancel.
  // DFT-0118: optional transactionId, propagated from useAddToWaitingListBayHireRequest's
  // runTransaction call.
  addToWaitingList: async (
    id: string,
    request: AddToWaitingListRequest = {},
    transactionId?: string,
  ): Promise<BayHireQueueRow> => {
    return api.post<BayHireQueueRow>(
      BAY_HIRE_BFF_SERVICE_SEGMENT,
      `bay-hire-requests/${id}:add-to-waiting-list`,
      request,
      { transactionId },
    );
  },

  // DFT-0009 claim. Task Management is authoritative for state; the response
  // returns the fresh queue row. Callers should refetch (never optimistic - AC48).
  // DFT-0118: optional transactionId, propagated from useClaimBayHireRequest's runTransaction call.
  claim: async (id: string, transactionId?: string): Promise<BayHireQueueRow> => {
    return api.put<BayHireQueueRow>(
      BAY_HIRE_BFF_SERVICE_SEGMENT,
      `bay-hire-requests/${id}:claim`,
      undefined,
      { transactionId },
    );
  },

  // DFT-0009 unclaim. Wrong-owner failures come back as 403 preserved from
  // Task Management (AC25). Callers should render but not act on state.
  // DFT-0118: optional transactionId, propagated from useUnclaimBayHireRequest's runTransaction call.
  unclaim: async (id: string, transactionId?: string): Promise<BayHireQueueRow> => {
    return api.put<BayHireQueueRow>(
      BAY_HIRE_BFF_SERVICE_SEGMENT,
      `bay-hire-requests/${id}:unclaim`,
      undefined,
      { transactionId },
    );
  },

  // DFT-0013 pre-approve (AC01, AC07). Requires an active CLAIMED task (AC12).
  // Pre-approval effects land asynchronously via the Temporal signal path
  // (AD-03); the response reflects the domain snapshot at request time.
  // DFT-0118: optional transactionId, propagated from usePreApproveBayHireRequest's
  // runTransaction call.
  preApprove: async (
    id: string,
    request: PreApproveRequest,
    transactionId?: string,
  ): Promise<BayHireQueueRow> => {
    return api.post<BayHireQueueRow>(
      BAY_HIRE_BFF_SERVICE_SEGMENT,
      `bay-hire-requests/${id}:pre-approve`,
      request,
      { transactionId },
    );
  },

  // DFT-0084: direct-approve an EXEMPTED request - same pattern as preApprove (finds the active
  // claimed Task Management task and completes it with an APPROVED decision), never entering
  // PRE_APPROVED. Effects land asynchronously via the Temporal signal path, same as preApprove.
  // DFT-0118: optional transactionId, propagated from useApproveExemptedBayHireRequest's
  // runTransaction call.
  approveExempted: async (
    id: string,
    request: ApproveExemptedRequest,
    transactionId?: string,
  ): Promise<BayHireQueueRow> => {
    return api.post<BayHireQueueRow>(
      BAY_HIRE_BFF_SERVICE_SEGMENT,
      `bay-hire-requests/${id}:approve-exempted`,
      request,
      { transactionId },
    );
  },

  // DFT-0013 (AD-04 phase 1, AC04): records attachment metadata (HOD approval, payment receipt,
  // supporting document) against an existing request. The returned attachment id is passed as
  // hodAttachmentId in the subsequent preApprove call.
  //
  // DFT-0050 (AD1/AD4): this JSON metadata call is now also the ONLY way a durable attachment's
  // documentUrl reaches the backend - the BFF relocates a freshly-staged temp/bay-hire/...
  // documentUrl to its permanent path before persisting (see uploadAttachmentFile below, which
  // does the SAS upload and then calls this).
  uploadAttachment: async (id: string, request: AttachmentUploadRequest): Promise<Attachment> => {
    return api.post<Attachment>(
      BAY_HIRE_BFF_SERVICE_SEGMENT,
      `bay-hire-requests/${id}/attachments`,
      request,
    );
  },

  // DFT-0013 follow-up, rewritten by DFT-0050 (AD1/AD3): uploads the actual file bytes directly
  // to Azure Blob Storage via a SAS token (mirroring uploadStagedAttachment above), then records
  // the resulting documentUrl via uploadAttachment's JSON metadata call - no bytes ever transit
  // bff-bay-hire-service or bay-hire-service (DFT-0012's original byte-proxy choice, superseded).
  uploadAttachmentFile: async (
    id: string,
    file: File,
    attachmentType: string,
  ): Promise<Attachment> => {
    const { blobUrl, sasToken } = await getSasToken({
      prefix: SasTokenPrefix.BAY_HIRE,
      contentType: file.type || null,
      privilege: SasTokenPrivilege.WRITE,
      blobUrl: null,
    });
    await uploadFileToAzureBlob({ file, blobUrl, sasToken, certificateType: attachmentType });
    return bayHireService.uploadAttachment(id, {
      attachmentType,
      documentUrl: blobUrl,
      originalFileName: file.name,
      contentType: file.type || undefined,
      fileSizeBytes: file.size,
    });
  },

  // DFT-0013 (AD-11, AC05): pass-through Reference Data bay options for the
  // pre-approval combobox.
  // DFT-0110: portCode (optional) scopes the catalogue to one port - the create form always
  // supplies it once a port is chosen, so a Shipping Agent is never offered a bay from the
  // OTHER port. Omitted entirely (not sent as an empty string) when absent, matching the
  // BFF/Reference Data contract's own optional-query-param convention.
  getBayOptions: async (portCode?: string): Promise<BayHireBayOption[]> => {
    return api.get<BayHireBayOption[]>(BAY_HIRE_BFF_SERVICE_SEGMENT, "bay-hire-bays", {
      ...(portCode ? { params: { portCode } } : {}),
    });
  },

  // DFT-0044 slice F (FE-01/FE-02): the caller's own company details, for the create form's
  // read-only "Company details" card - before any request exists to compose them onto (unlike
  // BayHireRequest.company, which is resolved from an EXISTING request's companyId).
  getMyCompanyDetails: async (): Promise<CompanyDetails> => {
    return api.get<CompanyDetails>(
      BAY_HIRE_BFF_SERVICE_SEGMENT,
      "bay-hire-requests/company-details",
    );
  },

  // DFT-0010 (AC01, AC28): Operations reject. Requires an active CLAIMED task.
  // Reject effects land asynchronously via the Temporal signal path (like preApprove);
  // the response reflects the domain snapshot at request time.
  // DFT-0118: optional transactionId, propagated from useRejectBayHireRequest's runTransaction call.
  rejectRequest: async (
    id: string,
    request: RejectRequest,
    transactionId?: string,
  ): Promise<BayHireQueueRow> => {
    return api.post<BayHireQueueRow>(
      BAY_HIRE_BFF_SERVICE_SEGMENT,
      `bay-hire-requests/${id}:reject`,
      request,
      { transactionId },
    );
  },

  // DFT-0010 (AC10, AC11): cancel. Path (Operations TM-complete vs Vendor
  // direct signal) is determined server-side by caller role.
  // DFT-0118: optional transactionId, propagated from useCancelBayHireRequest's runTransaction call.
  cancelRequest: async (
    id: string,
    request: CancelRequest,
    transactionId?: string,
  ): Promise<void> => {
    return api.post<void>(BAY_HIRE_BFF_SERVICE_SEGMENT, `bay-hire-requests/${id}:cancel`, request, {
      transactionId,
    });
  },

  // DFT-0010 (AC06, AC09): Vendor resubmit after rejection. No limit on
  // reject/resubmit cycles.
  // DFT-0118: optional transactionId, propagated from useResubmitBayHireRequest's runTransaction
  // call.
  resubmitRequest: async (
    id: string,
    request: ResubmitRequest,
    transactionId?: string,
  ): Promise<void> => {
    return api.post<void>(
      BAY_HIRE_BFF_SERVICE_SEGMENT,
      `bay-hire-requests/${id}:resubmit`,
      request,
      { transactionId },
    );
  },

  // DFT-0044 slice V: Vendor self-service correction of their own request while it is still
  // REQUESTED and unclaimed. A PATCH, not a POST action route like :resubmit/:cancel - the BFF's
  // own operation is a real PATCH /bay-hire-requests/{id}, and the domain returns the updated
  // request synchronously (no Temporal signal involved, unlike cancel/resubmit/receipt upload).
  // DFT-0118: optional transactionId, propagated from useUpdateBayHireRequest's runTransaction
  // call.
  updateRequest: async (
    id: string,
    request: VendorUpdateRequest,
    transactionId?: string,
  ): Promise<BayHireRequest> => {
    return api.patch<BayHireRequest>(
      BAY_HIRE_BFF_SERVICE_SEGMENT,
      `bay-hire-requests/${id}`,
      request,
      { transactionId },
    );
  },

  // DFT-0014: Vendor uploads a payment receipt for a chargeable,
  // pre-approved request. No active Task Management task exists for the
  // Vendor at PRE_APPROVED, so this signals the workflow directly
  // (mirrors :cancel's Vendor path).
  // DFT-0118: optional transactionId, propagated from useUploadBayHireReceipt's runTransaction
  // call.
  uploadReceipt: async (
    id: string,
    request: UploadReceiptRequest,
    transactionId?: string,
  ): Promise<void> => {
    return api.post<void>(
      BAY_HIRE_BFF_SERVICE_SEGMENT,
      `bay-hire-requests/${id}:upload-receipt`,
      request,
      { transactionId },
    );
  },

  // DFT-0014: Operations verifies a submitted payment receipt. Requires an
  // active claimed payment-verification Task Management task.
  // DFT-0118: optional transactionId, propagated from useVerifyBayHirePayment's runTransaction
  // call.
  verifyPayment: async (id: string, transactionId?: string): Promise<BayHireQueueRow> => {
    return api.post<BayHireQueueRow>(
      BAY_HIRE_BFF_SERVICE_SEGMENT,
      `bay-hire-requests/${id}:verify-payment`,
      undefined,
      { transactionId },
    );
  },

  // DFT-0014: Operations rejects a submitted payment receipt with a
  // mandatory reason. Reuses RejectRequest - no separate payment-rejection
  // request shape.
  // DFT-0118: optional transactionId, propagated from useRejectBayHirePayment's runTransaction
  // call.
  rejectPayment: async (
    id: string,
    request: RejectRequest,
    transactionId?: string,
  ): Promise<BayHireQueueRow> => {
    return api.post<BayHireQueueRow>(
      BAY_HIRE_BFF_SERVICE_SEGMENT,
      `bay-hire-requests/${id}:reject-payment`,
      request,
      { transactionId },
    );
  },
};
